/**
 * Spacecraft-centred body view (handover 7.1.2).
 *
 * Rendered in **inertial (ECI) axes**, not ECEF, and that choice is the whole point of the view:
 * a spin axis fixed in inertial space is the signature of a stable spin, and drawing it in a
 * rotating frame would smear that signature into a once-per-day circle. The backend serves
 * ECI-referenced quaternions and vectors from `/body_view`, so nothing here does frame maths.
 *
 * What an operator reads off this view:
 *   - body axes: where the spacecraft is pointing
 *   - omega arrow: instantaneous spin axis, length proportional to |omega|
 *   - unit-sphere trace: a point means pure spin, a circle means coning, a scribble means tumbling
 *   - sun / nadir / B vectors: the geometry behind a power or magnetometer event
 */

import * as THREE from 'three';

export const AXIS_COLOURS = {
  x: 0xff7f5a,
  y: 0x5ad07f,
  z: 0x5a9bff,
  omega: 0xf5f7fa,
  sun: 0xffc857,
  mag: 0xb05aff,
  nadir: 0x9aa4b2,
  velocity: 0x5ad0c0,
} as const;

export interface BodyViewData {
  times: number[]; // epoch milliseconds
  quatEciFromBody: THREE.Quaternion[];
  sunEci: THREE.Vector3[];
  nadirEci: THREE.Vector3[];
  velocityEci: THREE.Vector3[];
  bEci: THREE.Vector3[];
  omegaEci: THREE.Vector3[];
  omegaMagnitudeDegS: number[];
  valid: boolean[];
  attitudeSource: string;
}

export interface BodyViewToggles {
  bodyAxes: boolean;
  omega: boolean;
  spinTrace: boolean;
  sun: boolean;
  mag: boolean;
  nadir: boolean;
  velocity: boolean;
  model: boolean;
  solar: boolean;
}

const DEFAULT_TOGGLES: BodyViewToggles = {
  bodyAxes: true,
  omega: true,
  spinTrace: true,
  sun: true,
  mag: true,
  nadir: true,
  velocity: false,
  model: true,
  solar: false,
};

/** Measured string currents on the model: per-BCR series sharing one time base. */
export interface SolarData {
  times: number[]; // epoch milliseconds
  currents: Record<number, (number | null)[]>; // keyed by BCR number 1..7
}

/** Scene units: the unit sphere has radius 1 and the 3U body is drawn inside it. */
const SPHERE_RADIUS = 1.0;
const VECTOR_LENGTH = 1.35;

export class BodyView {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly root = new THREE.Group();
  private readonly bodyGroup = new THREE.Group();

  private readonly arrows: Record<string, THREE.ArrowHelper> = {};
  private readonly axisArrows: THREE.ArrowHelper[] = [];
  private spinTrace: THREE.Line | null = null;
  private spinTracePoint: THREE.Mesh | null = null;
  private sphere: THREE.Mesh | null = null;

  private data: BodyViewData | null = null;
  private toggles: BodyViewToggles = { ...DEFAULT_TOGGLES };
  private frameHandle = 0;
  private currentIndex = 0;
  private currentEpochMs = 0;

  // Solar string overlay: per-BCR mesh + material, measured currents, and labels.
  private readonly stringMaterials = new Map<number, THREE.MeshStandardMaterial>();
  private readonly stringLabels: THREE.Sprite[] = [];
  private solar: SolarData | null = null;
  private solarRefAmps = 0.05;

  // Simple orbit camera state; a full controls dependency is not worth it for one view.
  private azimuth = 0.9;
  private elevation = 0.45;
  private distance = 4.2;
  private dragging = false;
  private lastPointer: { x: number; y: number } | null = null;
  private readonly resizeObserver: ResizeObserver;

  constructor(private readonly container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(42, 1, 0.05, 200);
    this.scene.add(this.root);
    this.root.add(this.bodyGroup);

    this.buildStaticScene();
    this.attachPointerHandlers();
    // The pane resizes without the window doing so - a caveat bar appearing, the spacecraft
    // selector wrapping - and a window-resize listener alone leaves the canvas at its old size.
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    this.renderLoop();
  }

  // ------------------------------------------------------------------------------- scene setup

  private buildStaticScene(): void {
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.4));
    const key = new THREE.DirectionalLight(0xffffff, 1.9);
    key.position.set(3, 4, 5);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0x88aaff, 0.5);
    fill.position.set(-4, -2, -3);
    this.scene.add(fill);

    // Translucent unit sphere: the canvas the spin-axis trace is drawn on.
    const sphereGeom = new THREE.SphereGeometry(SPHERE_RADIUS, 48, 32);
    this.sphere = new THREE.Mesh(
      sphereGeom,
      new THREE.MeshBasicMaterial({
        color: 0x5a7090,
        transparent: true,
        opacity: 0.055,
        depthWrite: false,
        side: THREE.BackSide,
      }),
    );
    this.root.add(this.sphere);
    const wire = new THREE.LineSegments(
      new THREE.WireframeGeometry(new THREE.SphereGeometry(SPHERE_RADIUS, 16, 10)),
      new THREE.LineBasicMaterial({ color: 0x3d4d63, transparent: true, opacity: 0.35 }),
    );
    this.root.add(wire);

    this.buildSpacecraftModel();
    this.buildBodyAxes();

    for (const [name, colour] of [
      ['omega', AXIS_COLOURS.omega],
      ['sun', AXIS_COLOURS.sun],
      ['mag', AXIS_COLOURS.mag],
      ['nadir', AXIS_COLOURS.nadir],
      ['velocity', AXIS_COLOURS.velocity],
    ] as const) {
      const arrow = new THREE.ArrowHelper(
        new THREE.Vector3(0, 0, 1),
        new THREE.Vector3(),
        VECTOR_LENGTH,
        colour,
        0.16,
        0.08,
      );
      this.arrows[name] = arrow;
      this.root.add(arrow);
    }
  }

  /**
   * PHASMA as built, to the dimensions in the mission's own mechanical CAD
   * (gitlab.com/librespacefoundation/phasma/phasma-mech-assembly):
   *
   *   bus              100 x 100 x 340.5 mm   3U COTS structure
   *   deployed array   324 x 159 x 1 mm       '90deg deployed panel array dummy' sketch outline
   *   two of them                             'Satellite assembly' instantiates that part twice
   *
   * Each wing hinges on a side face along the edge it shares with the -Z face and deploys flush
   * into the -Z plane, so the array is one flat sheet offset from the body centreline rather
   * than two wings standing out from the bus.
   *
   * Body axes are the model axes, and they are the mission CAD's axes with no remap: +X along
   * the long (3U) dimension, +Z out of the S-band patch face, array facing -Z. Changing the
   * *shape* here is safe; changing which axis is which is not, because the telemetered attitude
   * is applied directly to the model root (docs/FRAMES.md section 4). Keep this in step with
   * tools/make_model.py, which builds the glTF the globe draws.
   */
  private buildSpacecraftModel(): void {
    // Metres, straight from the CAD, converted once. The 3U long dimension spans 0.55 scene
    // units inside the unit sphere, which is what sets the view's framing.
    const M = 0.55 / 0.3405;
    const BUS_LONG = 0.3405 * M; // along X
    const BUS_CROSS = 0.1 * M; // Y and Z
    const ARRAY_SPAN = 0.159 * M;
    const ARRAY_LENGTH = 0.324 * M;
    const ARRAY_THICKNESS = 0.001 * M;
    const RAIL = 0.0085 * M; // CubeSat standard rail width
    const FR4 = 0.09 * M; // the antenna board deployed off each end, into the -Z array plane

    const busMat = new THREE.MeshStandardMaterial({
      color: 0x8d99ab,
      metalness: 0.55,
      roughness: 0.45,
    });
    const bus = new THREE.Mesh(new THREE.BoxGeometry(BUS_LONG, BUS_CROSS, BUS_CROSS), busMat);
    this.bodyGroup.add(bus);
    bus.add(
      new THREE.LineSegments(
        new THREE.EdgesGeometry(bus.geometry),
        new THREE.LineBasicMaterial({ color: 0x1b2430 }),
      ),
    );

    // The four rails. They are what a CubeSat is held and ejected by, and drawing them proud of
    // the faces is the cheapest cue for which way the long axis runs.
    const railMat = new THREE.MeshStandardMaterial({
      color: 0xc3cbd8,
      metalness: 0.7,
      roughness: 0.3,
    });
    const railGeom = new THREE.BoxGeometry(BUS_LONG * 1.004, RAIL, RAIL);
    for (const sy of [1, -1]) {
      for (const sz of [1, -1]) {
        const rail = new THREE.Mesh(railGeom, railMat);
        rail.position.set(0, (sy * (BUS_CROSS - RAIL)) / 2, (sz * (BUS_CROSS - RAIL)) / 2);
        this.bodyGroup.add(rail);
      }
    }

    // Solar strings, one mesh per BCR so each can be tinted by its measured current. Placement
    // per config/spacecraft.toml [solar.strings], confirmed by operations 2026-08-06: bcr1 on
    // the -Z face, bcr2/bcr3 on the +Y/-Y side faces, and each wing hinged along the edge where
    // its side face meets the -Z face - so the deployed wing planes sit at z = -bus/2, the same
    // plane bcr1 is on, every wing cell facing -Z, split into inner (bcr4/bcr6) and outer
    // (bcr5/bcr7) panels. Keep in step with tools/make_model.py.
    const seg = ARRAY_SPAN / 2;
    const innerOffset = (BUS_CROSS + seg) / 2;
    const outerOffset = BUS_CROSS / 2 + seg * 1.5;
    const wingZ = -BUS_CROSS / 2;
    // The wings carry cells on their -Z side ONLY, so a wing string is a single-sided plane
    // facing -Z rather than a box - a box would paint cells on the +Z side too. The substrate
    // behind it is drawn separately in bus material, so the wing still reads as solid from
    // above without implying cells up there.
    const wingGeom = new THREE.PlaneGeometry(ARRAY_LENGTH, seg);
    const substrateGeom = new THREE.BoxGeometry(ARRAY_LENGTH, seg, ARRAY_THICKNESS);
    for (const sy of [1, -1]) {
      for (const off of [innerOffset, outerOffset]) {
        const sub = new THREE.Mesh(substrateGeom, railMat);
        sub.position.set(0, sy * off, wingZ + ARRAY_THICKNESS);
        this.bodyGroup.add(sub);
      }
    }

    const sideGeom = new THREE.BoxGeometry(BUS_LONG * 0.88, ARRAY_THICKNESS * 2, BUS_CROSS * 0.82);
    const strings: {
      bcr: number;
      geom: THREE.BufferGeometry;
      pos: [number, number, number];
      faceDown?: boolean;
    }[] = [
      {
        bcr: 1,
        geom: new THREE.BoxGeometry(BUS_LONG * 0.88, BUS_CROSS * 0.82, ARRAY_THICKNESS * 2),
        pos: [0, 0, -BUS_CROSS / 2],
      },
      { bcr: 2, geom: sideGeom, pos: [0, BUS_CROSS / 2, 0] },
      { bcr: 3, geom: sideGeom, pos: [0, -BUS_CROSS / 2, 0] },
      { bcr: 4, geom: wingGeom, pos: [0, innerOffset, wingZ], faceDown: true },
      { bcr: 5, geom: wingGeom, pos: [0, outerOffset, wingZ], faceDown: true },
      { bcr: 6, geom: wingGeom, pos: [0, -innerOffset, wingZ], faceDown: true },
      { bcr: 7, geom: wingGeom, pos: [0, -outerOffset, wingZ], faceDown: true },
    ];
    for (const { bcr, geom, pos, faceDown } of strings) {
      const material = new THREE.MeshStandardMaterial({
        color: 0x1d3f7a,
        metalness: 0.3,
        roughness: 0.35,
        emissive: 0x081428,
        // Single-sided on purpose: the back of a wing panel is substrate, not cells.
        side: THREE.FrontSide,
      });
      this.stringMaterials.set(bcr, material);
      const panel = new THREE.Mesh(geom, material);
      panel.position.set(...pos);
      // PlaneGeometry faces +Z; turn it over so the cells look along -Z.
      if (faceDown) panel.rotation.x = Math.PI;
      this.bodyGroup.add(panel);
      panel.add(
        new THREE.LineSegments(
          new THREE.EdgesGeometry(geom),
          new THREE.LineBasicMaterial({ color: 0x4a6ea8, transparent: true, opacity: 0.7 }),
        ),
      );
      // Annotation sprite, standing just off the panel along its outward direction.
      const label = makeLabel(`BCR-${bcr}`);
      const away = new THREE.Vector3(...pos);
      if (away.lengthSq() < 1e-9) away.set(0, 1, 0);
      away.normalize().multiplyScalar(0.09);
      label.position.set(pos[0] + away.x, pos[1] + away.y, pos[2] + away.z);
      label.visible = false;
      this.stringLabels.push(label);
      this.bodyGroup.add(label);
    }

    // The S-band patch is the only thing on the +Z face, which makes it the cue for which way
    // up the body frame sits.
    const patchMat = new THREE.MeshStandardMaterial({
      color: 0xc9a227,
      metalness: 0.6,
      roughness: 0.35,
    });
    const patch = new THREE.Mesh(
      new THREE.BoxGeometry(BUS_CROSS * 0.45, BUS_CROSS * 0.45, BUS_CROSS * 0.04),
      patchMat,
    );
    patch.position.set(0, 0, BUS_CROSS / 2);
    this.bodyGroup.add(patch);

    // A ~90 x 90 mm FR4 board deployed off each end face, flush into the -Z array plane. These
    // are antenna boards, not solar: they feed no BCR, so they are drawn in bus material and
    // never tinted by the solar overlay. Both carry a UHF element, whose length and form are
    // not documented - a thin rod represents the deployed span without inventing detail.
    const fr4Geom = new THREE.BoxGeometry(FR4, FR4, ARRAY_THICKNESS);
    const uhfGeom = new THREE.CylinderGeometry(RAIL * 0.12, RAIL * 0.12, 0.1 * M, 8);
    const uhfMat = new THREE.MeshStandardMaterial({
      color: 0xd8dee9,
      metalness: 0.8,
      roughness: 0.25,
    });
    for (const sx of [1, -1]) {
      const board = new THREE.Mesh(fr4Geom, railMat);
      board.position.set((sx * (BUS_LONG + FR4)) / 2, 0, -BUS_CROSS / 2);
      this.bodyGroup.add(board);

      const uhf = new THREE.Mesh(uhfGeom, uhfMat);
      uhf.rotation.z = Math.PI / 2;
      uhf.position.set(sx * (BUS_LONG / 2 + FR4 + 0.05 * M), 0, -BUS_CROSS / 2);
      this.bodyGroup.add(uhf);
    }

    // GNSS patch, on the underside of the E1 (+X) board so that it faces -Z. It is the only
    // feature that tells the two ends apart, and so the only thing fixing the sign of +X.
    const gnss = new THREE.Mesh(
      new THREE.BoxGeometry(FR4 * 0.28, FR4 * 0.28, ARRAY_THICKNESS * 6),
      patchMat,
    );
    gnss.position.set((BUS_LONG + FR4) / 2, 0, -(BUS_CROSS / 2 + ARRAY_THICKNESS * 3));
    this.bodyGroup.add(gnss);
  }

  private buildBodyAxes(): void {
    const dirs: [THREE.Vector3, number][] = [
      [new THREE.Vector3(1, 0, 0), AXIS_COLOURS.x],
      [new THREE.Vector3(0, 1, 0), AXIS_COLOURS.y],
      [new THREE.Vector3(0, 0, 1), AXIS_COLOURS.z],
    ];
    for (const [dir, colour] of dirs) {
      const arrow = new THREE.ArrowHelper(dir, new THREE.Vector3(), 0.85, colour, 0.12, 0.06);
      this.axisArrows.push(arrow);
      // Parented to the body so they rotate with the spacecraft.
      this.bodyGroup.add(arrow);
    }
  }

  // ---------------------------------------------------------------------------------- data

  setData(data: BodyViewData): void {
    this.data = data;
    this.rebuildSpinTrace();
  }

  /**
   * Drop all data and hide every data-driven element. Used when a load fails: the view must
   * show nothing rather than keep the previous window's attitude on screen, and it must never
   * fall back to an identity quaternion (handover 7.2).
   */
  clear(): void {
    this.data = null;
    if (this.spinTrace) {
      this.root.remove(this.spinTrace);
      this.spinTrace.geometry.dispose();
      this.spinTrace = null;
    }
    this.bodyGroup.visible = false;
    for (const arrow of this.axisArrows) arrow.visible = false;
    for (const arrow of Object.values(this.arrows)) arrow.visible = false;
    if (this.spinTracePoint) this.spinTracePoint.visible = false;
  }

  private rebuildSpinTrace(): void {
    if (this.spinTrace) {
      this.root.remove(this.spinTrace);
      this.spinTrace.geometry.dispose();
      this.spinTrace = null;
    }
    if (!this.data) return;

    const points: THREE.Vector3[] = [];
    for (let i = 0; i < this.data.omegaEci.length; i += 1) {
      if (!this.data.valid[i]) continue;
      const v = this.data.omegaEci[i]!;
      if (v.lengthSq() < 1e-9) continue;
      points.push(v.clone().multiplyScalar(SPHERE_RADIUS));
    }
    if (points.length < 2) return;

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    // Colour ramps along the window so the direction of travel is readable.
    const colours = new Float32Array(points.length * 3);
    for (let i = 0; i < points.length; i += 1) {
      const t = i / Math.max(points.length - 1, 1);
      colours[i * 3] = 0.35 + 0.6 * t;
      colours[i * 3 + 1] = 0.75 - 0.35 * t;
      colours[i * 3 + 2] = 0.95 - 0.25 * t;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
    this.spinTrace = new THREE.Line(
      geometry,
      new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.9 }),
    );
    this.root.add(this.spinTrace);

    if (!this.spinTracePoint) {
      this.spinTracePoint = new THREE.Mesh(
        new THREE.SphereGeometry(0.035, 16, 12),
        new THREE.MeshBasicMaterial({ color: 0xffffff }),
      );
      this.root.add(this.spinTracePoint);
    }
  }

  /** Move the view to a moment in time. Index lookup is a binary search over the sample times. */
  setTime(epochMs: number): void {
    this.currentEpochMs = epochMs;
    if (!this.data || this.data.times.length === 0) return;
    this.currentIndex = nearestIndex(this.data.times, epochMs);
  }

  /**
   * Measured solar string currents to paint the panels with. The reference current for full
   * brightness is the window's own maximum (floored at 50 mA), so the display is comparative:
   * a string that stays dark while its geometric twin glows is the deployment-failure
   * signature, regardless of season or sun distance.
   */
  setSolarData(solar: SolarData | null): void {
    this.solar = solar;
    let ref = 0.05;
    if (solar) {
      for (const series of Object.values(solar.currents)) {
        for (const v of series) if (v !== null && v > ref) ref = v;
      }
    }
    this.solarRefAmps = ref;
  }

  private applySolarTint(): void {
    const on = this.toggles.solar;
    for (const label of this.stringLabels) label.visible = on && this.bodyGroup.visible;

    if (!on || !this.solar || this.solar.times.length === 0) {
      for (const material of this.stringMaterials.values()) {
        material.emissive.setHex(0x081428);
        material.color.setHex(0x1d3f7a);
      }
      return;
    }
    const i = nearestIndex(this.solar.times, this.currentEpochMs);
    // WOD cadence is one sample a minute; beyond a couple of minutes there is no measurement
    // to show, and pretending otherwise would paint stale currents onto the model.
    const fresh = Math.abs(this.solar.times[i]! - this.currentEpochMs) < 150_000;
    for (const [bcr, material] of this.stringMaterials) {
      const value = fresh ? (this.solar.currents[bcr]?.[i] ?? null) : null;
      if (value === null) {
        // No measurement: neutral grey-blue, clearly distinct from "measured dark".
        material.color.setHex(0x2c3644);
        material.emissive.setHex(0x0a0e14);
        continue;
      }
      const frac = Math.min(1, Math.max(0, value / this.solarRefAmps));
      // Measured: near-black cell when producing nothing, ramping to hot amber at the
      // window's maximum. The sun arrow says which way light comes from; a lit-side panel
      // staying black is the thing this view exists to show.
      material.color.setHex(0x10151d);
      material.emissive.setRGB(1.0 * frac, 0.62 * frac, 0.12 * frac);
    }
  }

  setToggles(toggles: Partial<BodyViewToggles>): void {
    this.toggles = { ...this.toggles, ...toggles };
  }

  get sampleIndex(): number {
    return this.currentIndex;
  }

  /** Values at the current playhead, for the readout panel. */
  currentReadout(): {
    omegaDegS: number;
    valid: boolean;
    source: string;
  } | null {
    if (!this.data) return null;
    const i = this.currentIndex;
    return {
      omegaDegS: this.data.omegaMagnitudeDegS[i] ?? 0,
      valid: this.data.valid[i] ?? false,
      source: this.data.attitudeSource,
    };
  }

  // --------------------------------------------------------------------------------- render

  private applyFrame(): void {
    const data = this.data;
    if (!data) return;
    const i = this.currentIndex;
    const valid = data.valid[i] ?? false;

    const q = data.quatEciFromBody[i];
    if (q) this.bodyGroup.quaternion.copy(q);
    this.bodyGroup.visible = this.toggles.model && valid;
    for (const arrow of this.axisArrows) arrow.visible = this.toggles.bodyAxes && valid;

    const omega = data.omegaEci[i];
    const omegaArrow = this.arrows.omega!;
    if (omega && omega.lengthSq() > 1e-9) {
      omegaArrow.setDirection(omega);
      // Length encodes |omega| on a log scale: tumbling and residual rates differ by orders of
      // magnitude, and a linear scale makes the slow end invisible.
      const mag = data.omegaMagnitudeDegS[i] ?? 0;
      const scaled = 0.35 + 0.65 * Math.min(1, Math.log10(1 + mag * 9) / Math.log10(10));
      omegaArrow.setLength(SPHERE_RADIUS * scaled, 0.14, 0.07);
      omegaArrow.visible = this.toggles.omega && valid;
    } else {
      omegaArrow.visible = false;
    }

    this.pointArrow('sun', data.sunEci[i], this.toggles.sun);
    this.pointArrow('mag', data.bEci[i], this.toggles.mag);
    this.pointArrow('nadir', data.nadirEci[i], this.toggles.nadir);
    this.pointArrow('velocity', data.velocityEci[i], this.toggles.velocity);

    this.applySolarTint();

    if (this.spinTrace) this.spinTrace.visible = this.toggles.spinTrace;
    if (this.spinTracePoint) {
      this.spinTracePoint.visible = this.toggles.spinTrace && valid && !!omega;
      if (omega) this.spinTracePoint.position.copy(omega).multiplyScalar(SPHERE_RADIUS);
    }
    if (this.sphere) this.sphere.visible = this.toggles.spinTrace;
  }

  private pointArrow(name: string, dir: THREE.Vector3 | undefined, show: boolean): void {
    const arrow = this.arrows[name];
    if (!arrow) return;
    if (!dir || dir.lengthSq() < 1e-9) {
      arrow.visible = false;
      return;
    }
    arrow.setDirection(dir);
    arrow.visible = show;
  }

  private updateCamera(): void {
    const cosEl = Math.cos(this.elevation);
    this.camera.position.set(
      this.distance * cosEl * Math.cos(this.azimuth),
      this.distance * cosEl * Math.sin(this.azimuth),
      this.distance * Math.sin(this.elevation),
    );
    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(0, 0, 0);
  }

  private renderLoop = (): void => {
    this.frameHandle = requestAnimationFrame(this.renderLoop);
    this.applyFrame();
    this.updateCamera();
    this.renderer.render(this.scene, this.camera);
  };

  resize(): void {
    const width = Math.max(this.container.clientWidth, 1);
    const height = Math.max(this.container.clientHeight, 1);
    // updateStyle must stay on. setSize writes the drawing buffer as width * pixelRatio; with
    // updateStyle off, three.js leaves the CSS size unset and the canvas lays out at that buffer
    // size in CSS pixels instead, so on a 2x display it rendered at twice the pane and was
    // clipped by it. Letting three.js write style.width/height keeps buffer and layout in step.
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  // -------------------------------------------------------------------------------- pointer

  private attachPointerHandlers(): void {
    const el = this.renderer.domElement;
    el.style.touchAction = 'none';
    el.addEventListener('pointerdown', (e) => {
      this.dragging = true;
      this.lastPointer = { x: e.clientX, y: e.clientY };
      el.setPointerCapture(e.pointerId);
    });
    el.addEventListener('pointerup', (e) => {
      this.dragging = false;
      this.lastPointer = null;
      el.releasePointerCapture(e.pointerId);
    });
    el.addEventListener('pointermove', (e) => {
      if (!this.dragging || !this.lastPointer) return;
      const dx = e.clientX - this.lastPointer.x;
      const dy = e.clientY - this.lastPointer.y;
      this.lastPointer = { x: e.clientX, y: e.clientY };
      this.azimuth -= dx * 0.008;
      this.elevation = clamp(this.elevation + dy * 0.008, -1.45, 1.45);
    });
    el.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.distance = clamp(this.distance * (1 + Math.sign(e.deltaY) * 0.12), 1.6, 14);
      },
      { passive: false },
    );
  }

  destroy(): void {
    cancelAnimationFrame(this.frameHandle);
    this.resizeObserver.disconnect();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** A small billboard text label (used to annotate the solar strings). */
function makeLabel(text: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 40;
  const ctx = canvas.getContext('2d')!;
  ctx.font = '600 24px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(10, 14, 20, 0.55)';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#ffd98a';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 1);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false }),
  );
  sprite.scale.set(0.22, 0.069, 1);
  sprite.renderOrder = 10;
  return sprite;
}

/** Binary search for the sample nearest a timestamp. */
export function nearestIndex(sorted: number[], value: number): number {
  if (sorted.length === 0) return 0;
  let lo = 0;
  let hi = sorted.length - 1;
  if (value <= sorted[lo]!) return lo;
  if (value >= sorted[hi]!) return hi;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid]! <= value) lo = mid;
    else hi = mid;
  }
  return value - sorted[lo]! <= sorted[hi]! - value ? lo : hi;
}
